import Icon from '@ant-design/icons';
import { Spin, Switch } from 'antd';
import React, { RefObject, useRef, useState, useEffect, useCallback } from 'react';

import { CloseIconSvg, ContractArrowSvg, ExpandArrowSvg } from '@/assets/svg';

// Interfaz para el contexto combinado que recibe el componente
interface CombinedContext {
	// Propiedades del DashboardContext
	isExpandedParamsCard: boolean;
	showParamsCard: boolean;
	selectedSensors: any[];
	selectedDate: [any, any];

	// Propiedades del ExpandedParamsCardContext
	selectedSources: any[];
	selectedParamsList: any[];
	sourcesByParam: any[];
	isLoading: boolean;
	isSpinnerReadingVisible: boolean;
	primerRender: boolean;
}

// Interfaz para los setters combinados
interface CombinedContextSetters {
	// Setters del DashboardContext
	setIsExpandedParamsCard: (value: boolean) => void;
	setShowParamsCard: (value: boolean) => void;
}

export interface ExpandedParamsCardViewProps {
	context: CombinedContext;
	setContext: CombinedContextSetters;
	onCardHeightChange?: (height: number) => void;
	chartComponent: React.ReactNode;
	tableComponent: React.ReactNode;
	resumeTableComponent?: React.ReactNode; // New prop for ResumeTable
	sourcesListComponent: React.ReactNode;
	forecastSelectorComponent: React.ReactNode;
	switchShowSources: React.ReactNode;
	isLoading: boolean;
	topRef: RefObject<HTMLDivElement>;
	resetZoomComponent: React.ReactNode;
	strategiesSwitchComponent: React.ReactNode;
	strategiesChartComponent: React.ReactNode;
}

export const ExpandedParamsCardView = ({
	context,
	setContext,
	onCardHeightChange,
	chartComponent,
	tableComponent,
	resumeTableComponent,
	sourcesListComponent,
	forecastSelectorComponent,
	switchShowSources,
	isLoading,
	topRef,
	resetZoomComponent,
	strategiesSwitchComponent,
	strategiesChartComponent
}: ExpandedParamsCardViewProps) => {
	const [cardHeight, setCardHeight] = useState<number>(300);
	const [isDragging, setIsDragging] = useState<boolean>(false);

	const containerRef = useRef<HTMLDivElement>(null);
	const resizeHandleRef = useRef<HTMLDivElement>(null);
	const dragInfo = useRef<{ startY: number; startHeight: number; lastHeight?: number }>({
		startY: 0,
		startHeight: 300,
	});

	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		dragInfo.current = {
			startY: e.clientY,
			startHeight: cardHeight,
		};
		setIsDragging(true);

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		document.body.style.cursor = 'row-resize';
		document.body.style.userSelect = 'none';
	};

	const handleMouseMove = useCallback((e: MouseEvent) => {
		const deltaY = dragInfo.current.startY - e.clientY;
		const newHeight = dragInfo.current.startHeight + deltaY;
		const maxHeight = window.innerHeight * 0.5;
		const constrainedHeight = Math.max(300, Math.min(newHeight, maxHeight));
		setCardHeight(constrainedHeight);
		dragInfo.current.lastHeight = constrainedHeight;
	}, []);

	const handleMouseUp = useCallback(() => {
		setIsDragging(false);
		onCardHeightChange?.(dragInfo.current.lastHeight ?? dragInfo.current.startHeight);
		document.removeEventListener('mousemove', handleMouseMove);
		document.removeEventListener('mouseup', handleMouseUp);
		document.body.style.cursor = '';
		document.body.style.userSelect = '';
	}, [handleMouseMove, onCardHeightChange]);

	useEffect(() => {
		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};
	}, [handleMouseMove, handleMouseUp]);

	// Determine if we should show the component
	const shouldShow = !isLoading && context?.showParamsCard;

	// Determine layout mode
	const isCompactMode = !context?.isExpandedParamsCard && context?.showParamsCard;
	const isExpandedMode = context?.isExpandedParamsCard

	if (!shouldShow) {
		return null;
	}

	// Compact mode (similar to ParamsCardPresentational)
	if (isCompactMode) {
		const displayHeight = cardHeight;

		return (
			<div
				style={{
					position: 'fixed',
					bottom: 0,
					left: 0,
					right: 0,
					zIndex: 10,
					pointerEvents: 'none',
				}}
			>
			<div
				className="flex flex-col bg-[#f5f5f5] rounded-lg shadow-md"
				ref={containerRef}
				style={{
					height: `${displayHeight}px`,
					position: 'relative',
					overflow: 'visible',
					transition: 'height 0.3s ease-in-out',
					opacity: 1,
					visibility: 'visible',
					pointerEvents: 'auto',
				}}
			>
				{/* Resize handle */}
				<div
					className="resize-handle"
					ref={resizeHandleRef}
					onMouseDown={handleMouseDown}
					style={{
						height: '3px',
						cursor: 'row-resize',
						background: isDragging ? '#2563eb' : '#e5e7eb',
						position: 'absolute',
						top: '0',
						left: '0',
						right: '0',
						zIndex: 8,
						transition: 'background 0.2s',
						borderTopLeftRadius: '3px',
						borderTopRightRadius: '3px',
						opacity: 0.8,
						boxShadow: isDragging ? '0 0 3px rgba(0,0,0,0.3)' : 'none',
					}}
				/>

				{/* Two-column layout */}
				<div className="flex cols-gap-2 resizable" style={{ height: '100%' }}>
					{/* Left column - Resume Table */}
					<div
						className="flex column p-2 relative bg-white w-[40%] pb-3"
						style={{
							borderRadius: '0px 8px 8px 0px',
							height: '100%',
							overflow: 'auto',
							maxHeight: '100%',
						}}
					>
						{resumeTableComponent}
					</div>

					{/* Right column - Chart */}
					<div
						className="flex column pt-1 px-2 pb-2 w-[60%]"
						style={{
							backgroundColor: 'white',
							borderRadius: '8px 0px 0px 8px',
							position: 'relative',
							height: '100%',
							maxHeight: '100%',
							overflow: 'visible',
						}}
					>
						<div className="flex flex-col h-full" style={{ minHeight: '0' }}>
							<div className="flex items-center mb-1 ml-1 gap-2" style={{ flexShrink: 0, paddingTop: '8px' }}>
								{forecastSelectorComponent}
								{switchShowSources}
								
								<div
									className="flex justify-center items-center"
									style={{ position: 'absolute', top: '8px', right: '10px' }}
								>
									{resetZoomComponent}
									
									<Icon
										component={ExpandArrowSvg}
										className="mr-2 cursor-pointer"
										onClick={() => {
											setContext.setIsExpandedParamsCard(true);
										}}
									/>
									<Icon
										component={CloseIconSvg}
										className="cursor-pointer"
										onClick={() => {
											setContext.setShowParamsCard(false);
										}}
									/>
								</div>
							</div>
							<div
								className="flex flex-col"
								style={{ 
									minHeight: '0',
									flex: 1,
									overflow: 'visible'
								}}
							>
								{chartComponent}
							</div>
						</div>
					</div>
				</div>
			</div>
			</div>
		);
	}

	// Expanded mode (full screen)
	if (isExpandedMode) {
		return (
			<div
				style={{
					position: 'absolute',
					bottom: '-15px',
					width: '100%',
					height: 'calc(100vh - 50px)',
					backgroundColor: 'white',
					zIndex: 9,
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				{/* Header with controls */}
				<div
					className="flex items-center justify-between"
					style={{ 
						minHeight: '50px',
						padding: '10px 20px',
						backgroundColor: 'white',
						marginTop: '15px',
						flexShrink: 0,
						zIndex: 10,
					}}
					ref={topRef}
				>
					<div className="flex items-center gap-3">
						{context.isSpinnerReadingVisible && (
							<div className="spinner-container-expanded">
								<Spin size="large" tip="Processing data..." />
							</div>
						)}
						<div>{forecastSelectorComponent}</div>
						<div>{switchShowSources}</div>
						<div>{strategiesSwitchComponent}</div>
					</div>
					
					{/* Close/Contract buttons */}
					<div className="flex items-center gap-2">
						<Icon
							component={ContractArrowSvg}
							className="cursor-pointer"
							style={{ fontSize: '18px' }}
							onClick={() => {
								setContext.setIsExpandedParamsCard(false);
							}}
						/>
						<Icon
							component={CloseIconSvg}
							className="cursor-pointer"
							style={{ fontSize: '18px' }}
							onClick={() => {
								setContext.setShowParamsCard(false);
							}}
						/>
					</div>
				</div>

				{/* Main content - Optimized layout */}
				<div 
					className="flex flex-col gap-2 bg-white" 
					style={{ 
						flex: 1,
						// position: 'relative',
						// overflowY: 'scroll',
						padding: '20px',
						marginTop: '0px',
					}}
				>
					{/* Chart section - Takes most of the space */}
					<div className="flex">
						{chartComponent}
					</div>
					
					{/* strategiesChartComponent is now integrated in SynchronizedCharts */}
					{strategiesChartComponent && <div style={{ marginTop: '50px' }}>{strategiesChartComponent}</div>}
					
					{/* Sources section - Compact height */}
					
					{sourcesListComponent && <div className="flex flex-col gap-2 mb-2 mt-1">
						{sourcesListComponent}
					</div>}
					
					{/* Table section */}
					<div className="flex " style={{ flex: 1, minHeight: '180px', marginTop: '20px' }}>
						{tableComponent}
					</div>
				</div>
			</div>
		);
	}

	return null;
};
